import FabricAnalyticsListeners, { FabricChannel } from '@atlaskit/analytics-listeners';
import { HORIZONTAL_GLOBAL_NAV_HEIGHT } from '@atlaskit/atlassian-navigation';
import { Grid, GridColumn } from '@atlaskit/page';
import { LoggerContextProvider } from '@atlassian/notification-list';
import { inject, observer } from 'mobx-react';
import React, { lazy, Suspense, useEffect } from 'react';

import { getReleaseVersion } from '../config';
import { getViewPortDimensions } from '../facade/window';
import { AnalyticsConsumerProps, withAnalytics } from '../hoc/withAnalytics';
import { useFabricAnalyticsListenerAdapter } from '../hooks/useFabricAnalyticsListener';
import { captureException, captureMessage } from '../sentry';
import Store from '../store';
import UIStore from '../store/uiStore';
import UserStore from '../store/userStore';

import ErrorBoundary from './ErrorBoundary/ErrorBoundary';

type Props = {
  atlOrigin: string;
  uiStore: UIStore;
  userStore: UserStore;
  utmSource: string;
} & AnalyticsConsumerProps;

const NotificationFullPage = lazy(() => import('@atlassian/notification-full-page'));

const logger = {
  captureException: (error: Error, tags?: Record<string, string>): void => {
    captureException(error, tags);
  },
  captureMessage: (message: string, tags?: Record<string, string>): void => {
    captureMessage(message, tags);
  },
};

const tryParseBase64 = (maybeBase64: string): string | undefined => {
  try {
    return atob(maybeBase64);
  } catch {
    return undefined;
  }
};

const isValidJson = (maybeJsonString: string): boolean => {
  try {
    JSON.parse(maybeJsonString);
    return true;
  } catch {
    return false;
  }
};

const isValidAtlOrigin = (maybeBase64AtlOrigin: string | undefined): boolean => {
  const base64AtlOrigin = maybeBase64AtlOrigin || '';
  const plainTextAtlOrigin = tryParseBase64(base64AtlOrigin) || '';
  return isValidJson(plainTextAtlOrigin);
};

const sanitiseAtlOrigin = (rawAtlOrigin: string | undefined): string | undefined => {
  switch (true) {
    case !rawAtlOrigin:
    case rawAtlOrigin === 'none':
    case !isValidAtlOrigin(rawAtlOrigin):
      return undefined;
    default:
      return rawAtlOrigin;
  }
};

const NotificationsPage = observer((props: Props) => {
  const fabricAnalyticsListener = useFabricAnalyticsListenerAdapter();

  useEffect(() => {
    const { atlOrigin, utmSource } = props;

    props.analytics.page('notificationsScreen', {
      atlOrigin,
      utmSource,
      ...getViewPortDimensions(),
      release: getReleaseVersion(),
    });
  }, []);

  const handleFeedbackClick = () => {
    props.uiStore.setFeedbackOpen();
  };

  return (
    <Grid columns={16} spacing={props.uiStore.mobileMode ? 'compact' : 'comfortable'}>
      <GridColumn key="main-content" medium={16}>
        <div data-testid="notifications-content">
          <ErrorBoundary subject="notificationFullPage">
            <Suspense fallback={<div />}>
              <FabricAnalyticsListeners
                client={fabricAnalyticsListener}
                excludedChannels={[
                  FabricChannel.atlaskit,
                  FabricChannel.elements,
                  FabricChannel.editor,
                  FabricChannel.media,
                ]}
              >
                <LoggerContextProvider logger={logger}>
                  <NotificationFullPage
                    testId="uchi-notifications-full-page"
                    originId={sanitiseAtlOrigin(props.atlOrigin)}
                    styleOverrides={{
                      headerStickyTop: HORIZONTAL_GLOBAL_NAV_HEIGHT,
                      mobileHeaderStickyTop: HORIZONTAL_GLOBAL_NAV_HEIGHT,
                    }}
                    onFeedbackClick={handleFeedbackClick}
                    userId={props.userStore.accountId}
                    isGlobalStoreEnabled={true}
                  />
                </LoggerContextProvider>
              </FabricAnalyticsListeners>
            </Suspense>
          </ErrorBoundary>
        </div>
      </GridColumn>
    </Grid>
  );
});

export default inject(({ uiStore, userStore }: Store) => ({
  uiStore,
  userStore,
}))(withAnalytics(NotificationsPage));
